import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { FlagGroupService } from '../../../../flag/flag.group.service';
import { FlagService } from '../../../../flag/flag.service';
import { FlagGroup } from '../../../../shared/interfaces';
import { env } from '../../../../../environments/environment';

@Component({
  selector: 'app-flag-manager-edit-group',
  templateUrl: './flag-manager-edit-group.component.html',
  styleUrls: ['./flag-manager-edit-group.component.scss'],
})
export class FlagManagerEditGroupComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() allGroups = [];

  @ViewChild('modalRef', { static: false }) modalRef!: ModalDirective;
  @ViewChild('contentContainer') contentEl!: ElementRef<HTMLDivElement>;
  @ViewChild('scrollbarTrack') trackEl!: ElementRef<HTMLDivElement>;
  @ViewChild('scrollbarThumb') thumbEl!: ElementRef<HTMLDivElement>;
  private readonly TH = 496;
  private cleanup: (() => void) | null = null;

  get flagSubtypes() {
    const flagType = this.form.get('type').value;
    if (flagType) {
      return flagType.subtypes;
    } else {
      return [];
    }
  }

  constructor(
    private flagGroupService: FlagGroupService,
    private fb: UntypedFormBuilder,
    private flagService: FlagService,
  ) {
    this.form = this.fb.group({
      id: '',
      name: ['', [Validators.required, this.groupNameValidator()]],
      inspectionId: '',
      type: '',
      subtype: '',
      description: '',
      helpPictures: this.fb.array([]),

      violation: this.fb.group({
        elevatedRisk: '',
        deadline: '',
        whyDoesThisMatter: '',
        actionRequested: '',
        internalNotes: '',
        documentationRequested: '',
        urlTitle: '',
        url: '',
      }),

      discrepancy: this.fb.group({
        stated: '',
        actual: '',
        resultOfTheFinding: '',
        impact: '',
        effectiveDate: '',
        commentsForReport: '',
        urlTitle: '',
        url: '',
      }),
    });

    this.form.get('type').valueChanges.subscribe(type => {
      if (type) {
        this.form.get('subtype').setValue(type ? type.subtypes[0] : null);
      }
    });
  }

  form: UntypedFormGroup;
  @Output() groupSaved = new EventEmitter<any>();
  @Output() showGroupTemplate = new EventEmitter<void>();
  @Input() group: FlagGroup;
  @Input() readOnly = false;

  @ViewChild('helpPicturesModal', { static: true }) public helpPicturesModal: ModalDirective;
  fileToUpload: File;
  modalDateConfig: any;

  flagTypes;
  hideLegacy;
  saving = false;

  originalHelpPictures;

  async ngOnChanges(changes) {
    if (!this.flagTypes) {
      this.flagTypes = await this.flagService.listTypes();

      const subtypeInfo = await this.flagService.subtypeInfo;
      this.flagTypes.forEach(type => {
        type.subtypes.forEach(subtype => {
          subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, subtype.name).name;
        });
      });
    }

    if ('readOnly' in changes) {
      if (this.readOnly) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }

    if ('group' in changes) {
      this.isDetailOpen = false;
      this.modalDateConfig = {
        container: '#edit-group',
        adaptivePosition: true,
      };
      if (this.group) {
        this.group.helpPictures = this.group.helpPictures || [];
        this.form.patchValue(this.group);
        if (this.contentEl && this.contentEl.nativeElement) {
          this.contentEl.nativeElement.scrollTop = 0;
        }

        if (this.group.violation.deadline) {
          const dateStr = this.group.violation.deadline;
          const [year, month, day] = dateStr.split('-').map(Number);
          const utcDeadline = new Date(Date.UTC(year, month - 1, day, 12));
          this.form.get('violation.deadline').patchValue(utcDeadline);
        } else {
          this.form.get('violation.deadline').patchValue(null);
        }

        this.form
          .get('discrepancy.effectiveDate')
          .patchValue(this.group.discrepancy.effectiveDate ? new Date(this.group.discrepancy.effectiveDate) : null);

        if (this.group && this.group.subtype) {
          this.form.get('type').setValue(this.flagTypes.find(ft => ft.id === this.group.subtype.typeId));
          this.form.get('subtype').setValue(this.flagSubtypes.find(fst => fst.id === this.group.subtype.id));
          this.hideLegacy = this.group.subtype.name !== 'Legacy';
        }

        const wasDisabled = this.form.disabled;
        const helpPictures = this.form.get('helpPictures') as UntypedFormArray;
        while (helpPictures.length > 0) {
          helpPictures.removeAt(0);
        }

        if (this.group.helpPictures && this.group.helpPictures.length) {
          this.group.helpPictures.forEach(hp => {
            const group = this.fb.group({
              caption: wasDisabled ? [{ value: '', disabled: true }] : '',
              title: wasDisabled ? [{ value: '', disabled: true }] : '',
              ...Object.entries(hp).reduce((acc, [key, value]) => {
                acc[key] = wasDisabled ? [{ value, disabled: true }] : value;
                return acc;
              }, {} as any),
            });

            helpPictures.push(group);
          });
        }
      }
    }

    this.form.get('violation.deadline').disable();
  }

  ngAfterViewInit() {
    const c = this.contentEl.nativeElement;
    const tr = this.trackEl.nativeElement;
    const th = this.thumbEl.nativeElement;
    const TH = this.TH;
    const sync = () => {
      const ms = Math.max(c.scrollHeight - c.clientHeight, 0);
      const mt = Math.max(tr.clientHeight - TH, 0);
      th.style.top = `${ms ? (c.scrollTop / ms) * mt : 0}px`;
    };
    const drag = (e: PointerEvent) => {
      e.preventDefault();
      const rect = tr.getBoundingClientRect();
      const ms = Math.max(c.scrollHeight - c.clientHeight, 0);
      const mt = Math.max(tr.clientHeight - TH, 0);
      const move = (ev: PointerEvent) => {
        const y = Math.min(Math.max(0, ev.clientY - rect.top - TH / 2), mt);
        c.scrollTop = ms ? (y / mt) * ms : 0;
      };
      const up = () => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        this.cleanup = null;
      };
      move(e);
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      this.cleanup = up;
    };
    c.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    th.addEventListener('pointerdown', drag);
    tr.addEventListener('pointerdown', (e: any) => {
      if (e.target !== th) drag(e);
    });
    sync();
  }

  ngOnDestroy() {
    this.cleanup?.();
  }

  groupNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const groupName = control.value || '';
      const groupExistWithOtherSubtype = this.allGroups.find(
        g => g.name.toUpperCase() === groupName.toUpperCase() && g.name.toUpperCase() !== this.group.name.toUpperCase(),
      );
      if (groupExistWithOtherSubtype) {
        return { groupNameExists: true };
      }
      return null;
    };
  }

  async save() {
    if (this.saving) return;
    if (this.form.valid) {
      this.saving = true;
      const formData = this.form.value;
      const group = await this.flagGroupService.edit(this.group.id, formData);
      this.saving = false;
      this.groupSaved.emit(group);
    }
    if (this.contentEl && this.contentEl.nativeElement) {
      this.contentEl.nativeElement.scrollTop = 0;
    }
    this.closeModal();
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  setEffectiveDate(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.form.get('discrepancy.effectiveDate').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.form.get('discrepancy.effectiveDate').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.form.get('discrepancy.effectiveDate').setValue(d90);
        break;
    }
  }

  setDeadline(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.form.get('violation.deadline').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.form.get('violation.deadline').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.form.get('violation.deadline').setValue(d90);
        break;
    }
  }

  openDocPictures() {
    this.originalHelpPictures = (this.form.get('helpPictures') as UntypedFormArray).getRawValue();
    this.helpPicturesModal.show();
  }

  closeHelpPictures() {
    this.helpPicturesModal.hide();
  }

  cancelHelpPictures() {
    this.helpPicturesModal.hide();
    this.form.get('helpPictures').setValue(this.originalHelpPictures);
  }

  setUploadFile(files: FileList) {
    this.fileToUpload = files.item(0);
  }

  removeHelpPicture(i) {
    (this.form.get('helpPictures') as UntypedFormArray).removeAt(i);
  }

  getSrc(img) {
    return `${env.APIUrl}/image/download/${img.key}`;
  }

  async uploadHelpPicture(input: HTMLInputElement) {
    const formData = new FormData();
    formData.append('upload', this.fileToUpload, this.fileToUpload.name);
    const uploadInfo = await this.flagService.uploadPicture(formData);
    input.value = null;
    (this.form.get('helpPictures') as UntypedFormArray).push(
      this.fb.group({
        ...uploadInfo,
        caption: '',
        title: '',
      }),
    );
  }

  openModal(): void {
    this.modalRef.show();
  }

  closeModal(): void {
    if (this.contentEl && this.contentEl.nativeElement) {
      this.contentEl.nativeElement.scrollTop = 0;
    }
    this.modalRef.hide();
  }

  isDetailOpen: boolean = false;

  toggleDetail() {
    this.isDetailOpen = !this.isDetailOpen;
  }

  applyTemplate() {
    this.showGroupTemplate.emit();
    this.closeModal();
  }
}
